import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
} from "@chakra-ui/react";
import xhr from "xhr";
import TomarAsistencia from "./tomar-asistencia";



function getTodayRangeISO() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

function buildInFilter(path, values) {
  return (values || [])
    .map((v, i) => `${path}[${i}]=${encodeURIComponent(String(v))}`)
    .join("&");
}

export default function HomeAsistenciasTeacher() {
  const [loading, setLoading] = useState(true);
  const [teacherId, setTeacherId] = useState(null);
  const [liveClasses, setLiveClasses] = useState([]);
  const [selectedLiveClass, setSelectedLiveClass] = useState(null);

  // ===== 1) Obtener teacherId desde /users/me =====
  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const me = await xhr("/users/me?populate=teacher");
        const tid = me?.data?.teacher?.id ? Number(me.data.teacher.id) : null;
        setTeacherId(tid);
      } catch (e) {
        console.error("Error leyendo /users/me:", e);
        setTeacherId(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  // ===== 2) Cargar subjects del teacher filtrados por program_type.slug =====
  // ===== 3) Con esos subjectIds, cargar live-classes de HOY =====
  useEffect(() => {
    const run = async () => {
      if (!teacherId) {
        setLiveClasses([]);
        return;
      }

      setLoading(true);
      try {
        // Subjects del teacher, pero solo de los program_type permitidos
        // Subjects del teacher según el tipo de relación:
        // - Programas normales vienen por grade
        // - Carreras técnicas vienen por technical_career
        // - Cursos y diplomados vienen por course

        const gradeProgramTypeIn = buildInFilter(
          "filters[grade][program_type][slug][$in]",
          [
            "primaria-anual-para-ninos",
            "bachillerato-anual-para-adolescentes",
            "bachillerato-anual-de-usa-para-adolescentes",
          ]
        );

        const gradeSubjectQuery =
          `/subjects?` +
          `filters[teacher][id][$eq]=${teacherId}` +
          `&${gradeProgramTypeIn}` +
          `&pagination[limit]=-1` +
          `&populate[grade][populate][program_type]=*`;

        const technicalCareerSubjectQuery =
          `/subjects?` +
          `filters[teacher][id][$eq]=${teacherId}` +
          `&filters[technical_career][program_type][slug][$eq]=carreras-tecnicas` +
          `&pagination[limit]=-1` +
          `&populate[technical_career][populate][program_type]=*`;

        const courseSubjectQuery =
          `/subjects?` +
          `filters[teacher][id][$eq]=${teacherId}` +
          `&filters[course][program_type][slug][$eq]=cursos-y-diplomados` +
          `&pagination[limit]=-1` +
          `&populate[course][populate][program_type]=*`;

        const [resGradeSubjects, resTechnicalCareerSubjects, resCourseSubjects] =
          await Promise.all([
            xhr(gradeSubjectQuery),
            xhr(technicalCareerSubjectQuery),
            xhr(courseSubjectQuery),
          ]);

        const subjectIds = [
          ...(resGradeSubjects?.data?.data || []),
          ...(resTechnicalCareerSubjects?.data?.data || []),
          ...(resCourseSubjects?.data?.data || []),
        ]
          .map((s) => s?.id)
          .filter(Boolean);

        if (subjectIds.length === 0) {
          setLiveClasses([]);
          return;
        }

        // Live classes de hoy de esos subjects
        const subjectIn = buildInFilter("filters[subject][id][$in]", subjectIds);
        const { startISO, endISO } = getTodayRangeISO();

        const liveClassQuery =
          `/live-classes?` +
          `${subjectIn}` +
          `&filters[start_date][$gte]=${encodeURIComponent(startISO)}` +
          `&filters[start_date][$lte]=${encodeURIComponent(endISO)}` +
          `&pagination[limit]=-1` +
          `&sort[0]=start_date:asc` +
          `&populate[subject]=*` +
          `&populate[class_group]=*` +
          `&populate[grade][populate][program_type]=*` +
          `&populate[course][populate][program_type]=*` +
          `&populate[technical_career][populate][program_type]=*` +
          // Clase compartida: materias y grupos de otros programas
          `&populate[other_subjects][populate][grade][populate][program_type]=*` +
          `&populate[other_subjects][populate][course][populate][program_type]=*` +
          `&populate[other_subjects][populate][technical_career][populate][program_type]=*` +
          `&populate[other_class_groups][populate][0]=grade` +
          `&populate[other_class_groups][populate][1]=course` +
          `&populate[other_class_groups][populate][2]=technical_career`;

        const resLive = await xhr(liveClassQuery);
        setLiveClasses(resLive?.data?.data || []);
      } catch (e) {
        console.error("Error cargando subjects/live-classes:", e);
        setLiveClasses([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [teacherId]);

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={3}>
        <Box>
          <Text fontSize="24px" fontWeight="bold">
            Mis clases de hoy
          </Text>
          <Text fontSize="13px" color="gray.600">
            Primaria anual (niños), Bachillerato anual (adolescentes), Bachillerato USA (adolescentes), Carreras Técnicas y Cursos y diplomados
          </Text>
        </Box>

        {loading && (
          <Flex align="center" gap={2}>
            <Spinner size="sm" />
            <Text fontSize="13px">Cargando...</Text>
          </Flex>
        )}
      </Flex>

      {!teacherId ? (
        <Box p={3} border="1px solid" borderColor="gray.200" rounded="md">
          No se encontró el teacherId del usuario logeado.
        </Box>
      ) : (
        <TableContainer border="1px solid" borderColor="gray.200" rounded="md">
          <Table size="sm" variant="simple">
            <Thead bg="gray.100">
              <Tr>
                <Th width="120px">HORA</Th>
                <Th>MATERIA</Th>
                <Th width="220px">GRUPO</Th>
                <Th width="160px">TIPO</Th>
                <Th width="180px">ACCIÓN</Th>
              </Tr>
            </Thead>

            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={5}>
                    <Flex align="center" justify="center" p={6}>
                      <Spinner />
                    </Flex>
                  </Td>
                </Tr>
              ) : liveClasses.length === 0 ? (
                <Tr>
                  <Td colSpan={5}>
                    <Box p={3}>No tienes clases programadas para hoy.</Box>
                  </Td>
                </Tr>
              ) : (
                liveClasses.map((lc) => {
                  const start = lc?.start_date ? new Date(lc.start_date) : null;
                  const hour = start
                    ? start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : "—";

                  const subjectName =
                    lc?.subject?.name ||
                    lc?.subject?.title ||
                    `Subject #${lc?.subject?.id || "—"}`;

                  const groupName =
                    lc?.class_group?.name && lc.class_group.name !== "."
                      ? lc.class_group.name
                      : lc?.class_group?.id
                        ? `Grupo ${lc.class_group.id}`
                        : "—";

                  return (
                    <Tr key={lc.id}>
                      <Td>{hour}</Td>
                      <Td>{subjectName}</Td>
                      <Td>{groupName}</Td>
                      <Td>{lc?.type || "—"}</Td>
                      <Td>
                        <Button size="xs" colorScheme="blue" onClick={() => setSelectedLiveClass(lc)}>
                          Tomar asistencia
                        </Button>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {selectedLiveClass && (
        <TomarAsistencia liveClass={selectedLiveClass} onClose={() => setSelectedLiveClass(null)} />
      )}
    </Box>
  );
}
